import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import { safeHasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { TransactionListExportController } from '@app/feature/transaction/transaction-list-export/transaction-list-export.controller';
import { TRANSACTION_LIST_SORT_FIELD } from '@app/feature/transaction/transaction-list/transaction-list.config';
import { useCpmsBoGetTransactionsPaginatedQuery } from '@service/cpms-bo-transaction.service';

import {
  ChargePointChannelTransactionListContextProvider,
  useChargePointChannelTransactionListContext,
  useChargePointContext,
} from '../charge-point.context';
import * as css from './charge-point-channel-transaction-list.module.scss';
import { ChargePointChannelTransactionListView } from './charge-point-channel-transaction-list.view';

export const ChargePointChannelTransactionListController =
  (): React.JSX.Element => {
    // CONTEXT /////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList,
    } = useChargePointChannelTransactionListContext();

    // USER PERMISSIONS ////////////////////////////////////////////////////////

    const userPermissions = useAppSelector(permissions);

    // ACTIONS /////////////////////////////////////////////////////////////////

    const [actionExportIsDialogOpen, setActionExportIsDialogOpen] =
      useState(false);

    // DATA FETCHING ///////////////////////////////////////////////////////////

    const queryState = useCpmsBoGetTransactionsPaginatedQuery(
      getPaginatedQueryParameters,
    );

    const paginationState = useMemo(
      () =>
        getState({
          isLoading: queryState.isLoading,
          isError: queryState.isError,
          isFetching: queryState.isFetching,
        }),
      [queryState.isLoading, queryState.isError, queryState.isFetching],
    );

    // PAGE TITLE //////////////////////////////////////////////////////////////

    // Nothing to do

    // PAGE ACTIONS ////////////////////////////////////////////////////////////

    // Nothing to do

    // HANDLERS ////////////////////////////////////////////////////////////////

    const handlePaginationPageChange = (paginationPageNext: number) => {
      setGetPaginatedQueryParameters((current) => ({
        ...current,
        offset: (paginationPageNext - 1) * current.limit,
      }));
    };

    const handlePaginationLimitChange = (paginationLimitNext: number) => {
      setGetPaginatedQueryParameters((current) => ({
        ...current,
        limit: paginationLimitNext,
        offset: 0,
      }));
    };

    const handlePaginationSortingChange: OnChangeFn<SortingState> = (
      sortingState,
    ) => {
      setGetPaginatedQueryParameters((current) => {
        const paginationSorting0 = functionalUpdate(sortingState, [
          {
            desc: current.sortOrder === -1,
            id: current.sortField ?? TRANSACTION_LIST_SORT_FIELD,
          },
        ]).at(0);

        return {
          ...current,
          sortField:
            paginationSorting0?.id as GatewayGetTransactionsPaginatedModel['sortField'],
          sortOrder: paginationSorting0?.desc ? -1 : 1,
        };
      });
    };

    // RENDER //////////////////////////////////////////////////////////////////

    const {
      Component: ChargePointChannelTransactionListViewProtected,
      status: resourceStatus,
    } = withProtectedResource(
      ChargePointChannelTransactionListView,
      queryState,
      {
        isAuthorized: () =>
          safeHasAccessTo(userPermissions, {
            resourceName: Resource.TRANSACTIONS,
            action: ActionsByResource.TRANSACTIONS.READ,
          }),
        transformDataToResource: transformPaginatedResponse,
      },
    )({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id:
            getPaginatedQueryParameters.sortField ??
            TRANSACTION_LIST_SORT_FIELD,
        },
      ],
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
    });

    return (
      <Stack
        flex={1}
        className={css.listContainer}
        direction="column"
        gap={GapEnum.M}>
        {resourceStatus === ResourceStatus.SUCCESS && (
          <Stack justifyContent="end" gap={GapEnum.M}>
            <ButtonIcon
              icon="download-01"
              onClick={() => setActionExportIsDialogOpen(true)}
            />
          </Stack>
        )}
        {ChargePointChannelTransactionListViewProtected}
        {resourceStatus === ResourceStatus.SUCCESS && (
          <Dialog
            open={actionExportIsDialogOpen}
            onClose={() => setActionExportIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="download-01"
              title={t('$.transaction-list-export.title')}
              onClose={() => setActionExportIsDialogOpen(false)}
            />
            <Dialog.Content>
              <TransactionListExportController
                onCancel={() => setActionExportIsDialogOpen(false)}
                onSuccess={() => setActionExportIsDialogOpen(false)}
                chargePointId={getPaginatedQueryParameters.chargePointId}
              />
            </Dialog.Content>
          </Dialog>
        )}
      </Stack>
    );
  };

export const TransactionChannelListControllerWithContext =
  (): React.JSX.Element => {
    const chargePoint = useChargePointContext();

    return (
      <ChargePointChannelTransactionListContextProvider data={{ chargePoint }}>
        <ChargePointChannelTransactionListController />
      </ChargePointChannelTransactionListContextProvider>
    );
  };
